import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json'
  }
});

export const adminApi = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
    'X-Admin-Key': process.env.REACT_APP_ADMIN_KEY || '',
  }
});

// API functions
export const getAgents = () => api.get('/agents');
export const getClusters = () => api.get('/clusters');
export const getBusinesses = () => api.get('/businesses');
export const getBusiness = (id) => api.get(`/businesses/${id}`);
export const getEntries = (businessId, params = {}) => api.get(`/entries/${businessId}`, { params });
export const getUsers = (businessId, role = null) => api.get(`/users/${businessId}`, { params: { role } });
export const getSlips = (businessId) => api.get(`/slips/${businessId}`);
export const getAlerts = (businessId) => api.get(`/alerts/${businessId}`);
export const getTotals = (businessId, dateFrom = null, dateTo = null) =>
  api.get(`/totals/${businessId}`, { params: { date_from: dateFrom, date_to: dateTo } });
export const getUserByPhone = (phone) => api.get(`/user/phone/${encodeURIComponent(phone)}`);
export const autoRegisterUser = (phone) => api.post('/user/auto-register', { phone });
export const getConversations = (userId) => api.get(`/conversations/${userId}`);

// Auth
export const sendOTP = (phone) => api.post('/auth/send-otp', { phone });
export const verifyOTP = (phone, token) => api.post('/auth/verify-otp', { phone, token });

// Test chat (for development)
export const testChat = (phone, message) => api.post('/test/chat', { phone, message });

// Streaming chat (for web UI)
export const streamChat = async (phone, message, onChunk, onDone, onError, onHook) => {
  try {
    const response = await fetch(`${API}/chat/stream`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone, message })
    });

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop();

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.slice(6).trim();
          if (data === '[DONE]') {
            return;
          }
          try {
            const parsed = JSON.parse(data);
            if (parsed.type === 'chunk') {
              onChunk(parsed.text);
            } else if (parsed.type === 'ack' || parsed.type === 'progress') {
              onHook && onHook(parsed.text);
            } else if (parsed.type === 'done') {
              onDone(parsed.full_response);
            } else if (parsed.type === 'error') {
              onError(parsed.message);
            }
          } catch (e) {
            // Server occasionally emits incomplete SSE chunks during back-pressure.
            // Logging at debug level so production noise stays minimal but the
            // failure is observable in dev tools when streaming misbehaves.
            console.debug('[streamChat] skipped malformed SSE chunk:', e?.message || e);
          }
        }
      }
    }
  } catch (error) {
    onError(error.message);
  }
};

// Format amount in Indian style
export const formatAmount = (amount) => {
  if (!amount) return '₹0';
  return `₹${Number(amount).toLocaleString('en-IN')}`;
};

// Format date for display
export const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date(today - 86400000);
  
  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  
  return date.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
  });
};

// Format time
export const formatTime = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const getHealthScore = (businessId) => api.get(`/health/${businessId}`);
export const getInventory = (businessId, params = {}) => api.get(`/inventory/${businessId}`, { params });
export const getReports = (businessId, date) => api.get(`/reports/${businessId}`, { params: { date } });
export const exportReportsPdf = (businessId, date) =>
  `${API}/reports/${businessId}/pdf${date ? `?date=${date}` : ''}`;
export const exportInventoryPdf = (businessId, dateFrom, dateTo) => {
  const params = new URLSearchParams();
  if (dateFrom) params.set('date_from', dateFrom);
  if (dateTo) params.set('date_to', dateTo);
  return `${API}/inventory/${businessId}/pdf?${params.toString()}`;
};
export const updateBusiness = (businessId, data) => api.put(`/businesses/${businessId}`, data);

export default api;
